import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { FilterCollection } from "../../../services/FilterService";
import InvoiceService from "../../../services/InvoiceService";
import AdvancedFilters, {
  AdvancedFiltersProvider
} from "../../shared/components/AdvancedFilters";
import Table, { TableHeaderWithFieldId } from "../../shared/Table";
import InvoiceTableHeader from "./InvoiceTableHeader";
import InvoiceTableItem from "./InvoiceTableItem";
import InvoiceTableProvider from "./InvoiceTableProvider";
import InvoiceTableTotal from "./InvoiceTableTotal";
import InvoiceMassiveChanges from "./InvoiceMassiveChanges";
import ClientDetailContext from "../ClientDetailContext";
import TableContext from "../../task/TableContext";
import { DataType } from "../../../services/CompanyService";
import { InvoiceGroupContext } from "./providers/InvoiceTableProvider";
import { FixToTop } from "../../shared/FixToTop";
import { TranslationService } from "../../../services/TranslationService";
import { InvoiceList as InvoiceListAltType } from "./entities/InvoiceListResponse";
import {
  TableId,
  usesWidthContract
} from "../../shared/tableWidth/tableWidthRegistry";

type InvoiceListType = {
  client: {
    PersonId: number;
    Name: string;
  };
  groupId?: number;
  setReloads?: { invoice: () => void };
  reload: () => void;
};

const InvoiceList = ({
  client,
  groupId,
  setReloads = undefined,
  reload
}: InvoiceListType) => {
  const { invoiceIds, setInvoiceIds, setSelectedInvoiceData } =
    useContext(ClientDetailContext);
  const { ioTree } = useContext(InvoiceGroupContext);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  const [currentPageItems, setCurrentPageItems] = useState<
    { IOID: number; pending: number; CurrencyID: number }[]
  >([]);
  const currentPageIds = useMemo(
    () => currentPageItems.map((x) => x.IOID),
    [currentPageItems]
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef<number | null>(null);

  const toggleOneCheckbox = (
    invoiceId: number,
    checked: boolean,
    invoiceData?: InvoiceListAltType.Item
  ) => {
    if (scrollContainerRef.current) {
      // El body scrollable del Table ahora usa overflow-y-auto
      const scrollable = scrollContainerRef.current.querySelector(
        ".tableContainer.overflow-y-auto.overflow-x-auto"
      ) as HTMLDivElement;
      if (scrollable) {
        scrollTopRef.current = scrollable.scrollTop;
      }
    }

    let newIds: number[] = [];
    if (checked) {
      newIds = [...invoiceIds, invoiceId];
      // Almacenar datos cuando se selecciona
      if (invoiceData) {
        setSelectedInvoiceData((prev) => {
          const newMap = new Map(prev);
          newMap.set(invoiceId, {
            IOID: invoiceId,
            pending: invoiceData.pending,
            CurrencyID: invoiceData.CurrencyID
          });
          return newMap;
        });
      }
    } else {
      newIds = invoiceIds.filter((x) => x !== invoiceId);
      // Eliminar datos cuando se deselecciona
      setSelectedInvoiceData((prev) => {
        const newMap = new Map(prev);
        newMap.delete(invoiceId);
        return newMap;
      });
    }

    setInvoiceIds(newIds);
  };

  useEffect(() => {
    if (scrollContainerRef.current && scrollTopRef.current !== null) {
      // El body scrollable del Table ahora usa overflow-y-auto
      const scrollable = scrollContainerRef.current.querySelector(
        ".tableContainer.overflow-y-auto.overflow-x-auto"
      ) as HTMLDivElement;
      if (scrollable) {
        scrollable.scrollTop = scrollTopRef.current;
        scrollTopRef.current = null;
      }
    }
  }, [invoiceIds]);

  useEffect(() => {
    if (invoiceIds.length === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [invoiceIds.length, showSelectedOnly]);

  useEffect(() => {
    if (!isExpandedOpen) {
      return;
    }
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpandedOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isExpandedOpen]);

  //const showToggleCheckbox = CompanyService.canDo("editio") ? toggleOneCheckbox : undefined;

  const tableHeaders = useMemo(() => {
    const toggleAllCheckboxes = (event: ChangeEvent<HTMLInputElement>) => {
      const checked = (event.target as HTMLInputElement).checked;
      if (!checked && showSelectedOnly) {
        setInvoiceIds([]);
        setSelectedInvoiceData(new Map());
        setShowSelectedOnly(false);
      } else if (checked) {
        setInvoiceIds((prev: number[]) => [
          ...new Set([...prev, ...currentPageIds])
        ]);
        setSelectedInvoiceData((prev) => {
          const newMap = new Map(prev);
          currentPageItems.forEach((item) => newMap.set(item.IOID, item));
          return newMap;
        });
      } else {
        setInvoiceIds((prev: number[]) =>
          prev.filter((id) => !currentPageIds.includes(id))
        );
        setSelectedInvoiceData((prev) => {
          const newMap = new Map(prev);
          currentPageIds.forEach((id) => newMap.delete(id));
          return newMap;
        });
      }
    };
    const headers = InvoiceService.getTableHeaders(
      true,
      true,
      TableId.InvoiceList
    );
    const widthContract = usesWidthContract(TableId.InvoiceList);
    const allPageSelected =
      currentPageIds.length > 0 &&
      currentPageIds.every((id) => invoiceIds.includes(id));
    headers.unshift(
      new TableHeaderWithFieldId(
        "Checkbox",
        () => (
          <input
            type="checkbox"
            onChange={toggleAllCheckboxes}
            checked={allPageSelected}
            disabled={showSelectedOnly}
          />
        ),
        false,
        false,
        "checkbox",
        DataType.IOStatus,
        widthContract ? "col-checkbox col-center" : "w-30px"
      )
    );
    //}
    headers.push(
      new TableHeaderWithFieldId(
        "download",
        "",
        false,
        false,
        "download",
        DataType.Download,
        widthContract ? "col-icon col-center" : ""
      )
    );
    if (widthContract) {
      headers.forEach((header) => {
        header.widthContract = true;
      });
    }
    return headers;
  }, [
    showSelectedOnly,
    setInvoiceIds,
    setSelectedInvoiceData,
    currentPageIds,
    currentPageItems,
    invoiceIds
  ]);

  const selectedInvoiceIds =
    showSelectedOnly && invoiceIds.length > 0 ? invoiceIds : undefined;

  return (
    <InvoiceTableProvider
      clientId={client.PersonId}
      groupId={groupId}
      selectedInvoiceIds={selectedInvoiceIds}
      onPageLoaded={setCurrentPageItems}
    >
      <AdvancedFiltersProvider>
        <TableContext.Consumer>
          {({ reload }) => {
            if (setReloads) {
              setReloads.invoice = reload;
            }
            return <></>;
          }}
        </TableContext.Consumer>
        <div className="backPublicSmooth pb-3">
          <div className="pt-3 client-detail-section-title">
            {TranslationService.translate.Invoices}
          </div>
        </div>
        <span id="invoice-header-marker" />

        <FixToTop id="fixTopInvoices">
          <InvoiceTableHeader
            client={client}
            ioTreeFilters={ioTree}
            reload={() =>
              setReloads ? Object.values(setReloads).map((x) => x()) : undefined
            }
            onExpand={() => setIsExpandedOpen(true)}
          />
          <div className="mx-0">
            <AdvancedFilters page={FilterCollection.Invoice} />
          </div>
          <InvoiceMassiveChanges
            personId={client.PersonId}
            showSelectedOnly={showSelectedOnly}
            setShowSelectedOnly={setShowSelectedOnly}
          />
        </FixToTop>

        <div ref={scrollContainerRef} className="invoice-list-container">
          <Table
            headers={tableHeaders}
            item={InvoiceTableItem(
              toggleOneCheckbox,
              tableHeaders,
              reload,
              TableId.InvoiceList
            )}
            stickyHeader={true}
            tableId={TableId.InvoiceList}
          >
            <InvoiceTableTotal tableHeaders={tableHeaders} />
          </Table>
        </div>

        {isExpandedOpen &&
          createPortal(
            <div
              className="invoice-expanded-overlay"
              onClick={() => setIsExpandedOpen(false)}
            >
              <div
                className="invoice-expanded-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <InvoiceTableHeader
                  client={client}
                  ioTreeFilters={ioTree}
                  reload={() =>
                    setReloads
                      ? Object.values(setReloads).map((x) => x())
                      : undefined
                  }
                  onExpand={() => setIsExpandedOpen(true)}
                  isExpandedView
                  onClose={() => setIsExpandedOpen(false)}
                />
                <div className="invoice-expanded-body">
                  <div className="mx-0">
                    <AdvancedFilters page={FilterCollection.Invoice} />
                  </div>
                  <InvoiceMassiveChanges
                    personId={client.PersonId}
                    showSelectedOnly={showSelectedOnly}
                    setShowSelectedOnly={setShowSelectedOnly}
                  />
                  <Table
                    headers={tableHeaders}
                    item={InvoiceTableItem(
                      toggleOneCheckbox,
                      tableHeaders,
                      reload,
                      TableId.InvoiceList
                    )}
                    stickyHeader={true}
                    tableId={TableId.InvoiceList}
                    additionalClass="invoice-expanded-tbody"
                  >
                    <InvoiceTableTotal tableHeaders={tableHeaders} />
                  </Table>
                </div>
              </div>
            </div>,
            document.body
          )}
      </AdvancedFiltersProvider>
    </InvoiceTableProvider>
  );
};

export default InvoiceList;
